import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Modal from "@material-ui/core/Modal";
import Button from "@material-ui/core/Button";
import { SEASON_START } from "../../../common/flags";
import { isClassAllowedForSchool } from "../../../common/schoolClasses";
import { isPhoneValid, PHONE_FORMAT_HINT } from "../../../common/phone";
import { updateUserById } from "../../../store/actions/generic";
import { validateToken } from "../../../store/actions/auth";
import { actionCreate } from "../../../store/actions/actionCreator";
import { actionTypeSuccess, SIGN_IN } from "../../../store/actionTypes";
import { setAuthHeadersToLocalStorage } from "../../../helpers/localStorageHelper";

// Öğrenci ve öğretmenler bu sezon bilgilerini onaylamadıysa onay ekranı gösterilir.
export const needsInfoConfirmation = (user) =>
  [0, 1].includes(user.role) &&
  (!user.info_confirmed_at || new Date(user.info_confirmed_at) < SEASON_START);

const InfoRow = ({ label, text }) => (
  <div style={{ marginBottom: 8 }}>
    <span style={{ fontWeight: "bold" }}>{label}: </span>
    <span>{text || "-"}</span>
  </div>
);

export const InfoConfirmationModal = ({ user, onEditClick }) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  // Güncelleme süresi dolduysa backend deny_message döner. O durumda ekranı kapatıyoruz ki kullanıcı kilitli kalmasın.
  const [isDenied, setIsDenied] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  if (isDenied) return null;

  const schoolName = user.school_name
    ? user.school_name + " (" + user.city + "/" + user.district + ")"
    : "";

  const coachClassIds = user.all_coaches_of_this_school
    .filter((c) => c.id === user.id)
    .map((c) => c.class_id)
    .sort((a, b) => a - b);
  const coachClasses = coachClassIds
    .map((classId) => classId + ". Sınıf")
    .join(", ");

  // Sınıf bilgisi okulun kademesine uymuyorsa (eski hatalı veri) "Bilgilerim Güncel" ile geçilemez,
  // önce düzenlenmesi gerekir. Backend de bu durumda onayı kaydetmiyor.
  const isClassInfoValid =
    user.role === 0
      ? !!user.class_id &&
        isClassAllowedForSchool(user.class_id, user.school_name)
      : coachClassIds.every((c) =>
          isClassAllowedForSchool(c, user.school_name),
        );

  const classMismatchMessage =
    user.role === 0
      ? "Sınıf bilginiz okulunuzun kademesiyle uyuşmuyor. Lütfen \"Bilgilerimi Düzenle\" butonu ile sınıfınızı seçiniz."
      : "Sınıflarınız arasında okulunuzun kademesine uymayanlar var. Lütfen \"Bilgilerimi Düzenle\" butonu ile sınıflarınızı kontrol edip kaydediniz.";

  // Telefon kayıt formundaki biçimde değilse (eski kayıtlar) önce düzenlenmesi gerekir.
  const isPhoneInfoValid = isPhoneValid(user.phone);
  const phoneInvalidMessage =
    `Telefon numaranız eksik ya da hatalı. Lütfen "Bilgilerimi Düzenle" butonu ile telefon numaranızı ${PHONE_FORMAT_HINT} biçiminde güncelleyiniz.`;

  const onConfirmClick = () => {
    if (!isPhoneInfoValid) {
      setErrorMessage(phoneInvalidMessage);
      return;
    }
    if (!isClassInfoValid) {
      setErrorMessage(classMismatchMessage);
      return;
    }
    setErrorMessage("");
    setIsSaving(true);
    // Sadece user_id gönderiyoruz; backend, kullanıcı kendi kaydını güncellediğinde info_confirmed_at'i doldurur.
    // class_coaches göndermiyoruz: gönderilmezse backend öğretmenin sınıf atamalarına dokunmaz.
    dispatch(updateUserById({ user_id: user.id })).then((res) => {
      setIsSaving(false);
      if (!res || res.error) {
        alert(
          "Onay kaydedilemedi. Oturumunuzun süresi dolmuş olabilir; gerekirse lütfen tekrar giriş yapınız.",
        );
        // Oturum geçersizse validateToken kullanıcıyı çıkış yaptırır ve giriş sayfasına yönlendirir.
        dispatch(validateToken());
        return;
      }
      if (res.deny_message) {
        alert(res.deny_message);
        setIsDenied(true);
        return;
      }
      setAuthHeadersToLocalStorage({ user: JSON.stringify(res) });
      dispatch(actionCreate(actionTypeSuccess(SIGN_IN), { user: res }));
      // Backend sınıf bilgisi kademeye uymadığı için onayı kaydetmediyse ekran açık kalır, sebebini gösteriyoruz.
      if (needsInfoConfirmation(res)) setErrorMessage(classMismatchMessage);
    });
  };

  return (
    <Modal
      style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      open={true}
      aria-labelledby="info-confirmation-title"
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: 8,
          width: "100%",
          maxWidth: 520,
          padding: 24,
          outline: "none",
        }}
      >
        <h3 id="info-confirmation-title" style={{ marginTop: 0 }}>
          Yeni Sezon Bilgi Onayı
        </h3>
        <p>
          Yeni sezon başladı. Lütfen aşağıdaki bilgilerinizin güncel olduğunu
          kontrol ediniz. Değişiklik varsa "Bilgilerimi Düzenle" butonu ile
          güncelleyebilirsiniz.
        </p>

        <InfoRow label="Okul" text={schoolName} />
        <InfoRow label="Adı-Soyadı" text={user.name} />
        <InfoRow label="E-Posta" text={user.email} />
        <InfoRow label="Telefon" text={user.phone} />
        {!isPhoneInfoValid && (
          <div style={{ color: "#c62828", fontSize: 14, marginTop: -4, marginBottom: 8 }}>
            Telefon numaranızın güncellenmesi gerekiyor.
          </div>
        )}
        {user.role === 0 && (
          <InfoRow label="Kategori" text={user.class_id && user.class_id + ". Sınıf"} />
        )}
        {user.role === 1 && <InfoRow label="Sınıflarınız" text={coachClasses} />}

        {errorMessage && (
          <p style={{ color: "#c62828", fontWeight: "bold", marginBottom: 0 }}>
            {errorMessage}
          </p>
        )}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 24,
          }}
        >
          <Button variant="outlined" onClick={onEditClick} disabled={isSaving}>
            Bilgilerimi Düzenle
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirmClick}
            disabled={isSaving}
          >
            {isSaving ? "Kaydediliyor..." : "Bilgilerim Güncel"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
